import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import useUserRoles from "hooks/useUserRoles";

import EditBien from "./editBien";

import ProcesosApi from "api/procesosApi";

const TabBienes = ({ proceso }) => {
  const { user, roles } = useUserRoles();
  const [bienes, setBienes] = useState([]);
  const [selectedBien, setSelectedBien] = useState(null);
  const [showBienModal, setShowBienModal] = useState(false);

  const fetchDatosIniciales = async () => {
    try {
      const bienesResponse = await ProcesosApi.obtenerBienesProceso(proceso.id);
      setBienes(bienesResponse.data?.bienes);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
    }
  };

  useEffect(() => {
    if (proceso) {
      fetchDatosIniciales();
    }
  }, [user, proceso]);

  const formatCurrency = (value) => {
    const number = value ? value.replace(/\D/g, "") : "0";
    const numberValue = Number(number);
    const formatter = new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return {
      formatted: formatter.format(number || 0).replace(/\s/g, ""),
      value: numberValue,
    };
  };

  const handleEliminarBien = async (bien) => {
    if (window.confirm("¿Está seguro(a) de eliminar este Bien?")) {
      try {
        await ProcesosApi.eliminarBien(proceso.id, bien.id);
        fetchDatosIniciales();
      } catch (error) {
        console.error("Error al eliminar el Proceso Judicial.", error);
      }
    }
  };

  const bienesArray = Array.isArray(bienes) ? bienes : [];
  const bienesPorTipo = bienesArray.reduce((acc, bien) => {
    const tipo = bien.tipo_bien;
    if (!acc[tipo]) {
      acc[tipo] = {
        bienes: [],
        totalAvaluoCatastral: 0,
      };
    }
    acc[tipo].bienes.push(bien);
    acc[tipo].totalAvaluoCatastral += bien.avaluo_catastral || 0;
    return acc;
  }, {});
  //const totalGeneralAvaluoCatastral = bienesArray.reduce((sum, bien) => sum + (bien.avaluo_catastral || 0), 0);

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6} sx={{ mt: 1, display: "flex", justifyContent: "flex-start", gap: 2 }}>
          {(roles.isAdmin || roles.isCoordinador || roles.isOperador || roles.isDirector) && (
            <SuiButton variant="gradient" buttonColor="info" onClick={() => setShowBienModal(true)}>
              Agregar Bien
            </SuiButton>
          )}
          <EditBien
            open={showBienModal}
            onClose={() => setShowBienModal(false)}
            proceso={proceso}
            bien={selectedBien}
            onSuccess={() => {
              fetchDatosIniciales();
              setShowBienModal(false);
            }}
          />
        </Grid>
      </Grid>
      {Object.entries(bienesPorTipo).map(([tipo, data]) => {
        return (
          <Box key={tipo} sx={{ mt: 1 }}>
            <SuiTypography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              {tipo}
            </SuiTypography>
            <Grid container spacing={1}>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                      >
                        Opciones
                      </TableCell>
                      {tipo === "MUEBLE" ? (
                        <>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Tipo
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Marca
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Placa
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Color
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Avalúo
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Afectaciones
                          </TableCell>
                        </>
                      ) : tipo === "INMUEBLE" ? (
                        <>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Tipo
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Dirección
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Matrícula Inmobiliaria
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Avalúo
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "left", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            Afectaciones
                          </TableCell>
                        </>
                      ) : null}
                    </TableRow>
                    {data.bienes.map((bien) => (
                      <TableRow key={bien.id}>
                        <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedBien(bien);
                              setShowBienModal(true);
                            }}
                            sx={{
                              mr: 1,
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.05)",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              handleEliminarBien(bien);
                            }}
                            sx={{
                              mr: 1,
                              "&:hover": {
                                backgroundColor: "rgba(255, 0, 0, 0.1)", // Un rojo suave al pasar el mouse
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        {tipo === "MUEBLE" ? (
                          <>
                            <TableCell sx={{ padding: "2px 2px" }}>{bien.tipo_mueble}</TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>{bien.marca}</TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>{bien.placa}</TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>{bien.color}</TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>
                              {formatCurrency(String(bien.avaluo_catastral)).formatted}
                            </TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>{bien.afectaciones}</TableCell>
                          </>
                        ) : tipo === "INMUEBLE" ? (
                          <>
                            <TableCell sx={{ padding: "2px 2px" }}>{bien.tipo_inmueble}</TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>{bien.direccion}</TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>
                              {bien.matricula_inmobiliaria}
                            </TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>
                              {formatCurrency(String(bien.avaluo_catastral)).formatted}
                            </TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>{bien.afectaciones}</TableCell>
                          </>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

TabBienes.propTypes = {
  proceso: PropTypes.object,
};

export default TabBienes;
